import { z } from 'zod';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';
import { employmentEpisodeIdSchema, withEpisode } from '../episodeAddress';

/**
 * Validation schema for no-show
 */
const markNoShowSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  plannedStart: z.iso.date('Planned start must be in ISO format (YYYY-MM-DD)').optional(),
  reason: z.string().optional(),
  // ADR-136 phase 5 (§2a settlement #3): true when the no-show person has a
  // scheduled contract end — cancel it FIRST via resign.withdrawn, otherwise
  // the orphaned Pending TERMINATION_FINALIZED would later be enacted by the
  // sweep against a voided person.
  withdrawScheduledEnd: z.boolean().optional()
});

/**
 * Mark Employee as No-Show
 *
 * @param {Object} payload - No-show data
 * @param {string} payload.employeeId - Required
 * @param {string} [payload.plannedStart] - Optional - ISO date format
 * @param {string} [payload.reason] - Optional
 * @param {boolean} [payload.withdrawScheduledEnd] - ADR-136: void an FTC person
 *   as the batch [resign.withdrawn, hire.no_show] — one transaction, full trail.
 * @param {string} [actorType='User'] - Actor type (System, User, Import)
 * @returns {Promise<Object>}
 * @throws {z.ZodError} - If validation fails
 */
export const markNoShow = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  // Validate payload
  const validatedPayload = markNoShowSchema.parse(payload);

  const events = [];

  if (validatedPayload.withdrawScheduledEnd) {
    events.push({
      type: EMPLOYMENT_EVENT_TYPES.RESIGNATION_WITHDRAWN,
      payload: {
        employeeId: validatedPayload.employeeId,
      ...withEpisode(validatedPayload),
        reason: `Scheduled contract end withdrawn — employee marked as no-show. ${validatedPayload.reason || ''}`.trim()
      }
    });
  }

  events.push({
    type: EMPLOYMENT_EVENT_TYPES.NO_SHOW,
    payload: {
      employeeId: validatedPayload.employeeId,
      ...withEpisode(validatedPayload),
      ...(validatedPayload.plannedStart ? { plannedStart: validatedPayload.plannedStart } : {}),
      ...(validatedPayload.reason ? { reason: validatedPayload.reason } : {})
    }
  });

  return await employmentApi.triggerEmploymentEvents({
    metadata: {
      actorType
    },
    events
  });
};
